import Vue from 'vue'
import BootstrapVue from 'bootstrap-vue'

import Vuelidate from 'vuelidate'
import FormulatePlugin from '@/plugins/FormulatePlugin'
import McToastAlertPlugin from '@/plugins/McToastAlertPlugin'
import McMessageDialogPlugin from '@/plugins/McMessageDialogPlugin'
import * as VueGoogleMaps from 'vue2-google-maps'
import PortalVue from 'portal-vue'

import 'maplibre-gl/dist/maplibre-gl.css'

import VueRouter from 'vue-router'
import router from './router/index'
import vco from 'v-click-outside'
import VueSweetalert2 from 'vue-sweetalert2'

import '../src/design/app.scss'

import store from '@/state/store'
import '@/services/upload/register-handlers'

import App from './App.vue'

import i18n from './i18n'

import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/id'
import McLoading from '@/components/McLoading'
import McMessageDialog from '@/components/McMessageDialog'

import GmapCluster from 'vue2-google-maps/src/components/cluster'

import VuejsDialog from 'vuejs-dialog'
// include the default style
import 'vuejs-dialog/dist/vuejs-dialog.min.css'

import Toast from 'vue-toastification'
// Import the CSS or use your own!
import 'vue-toastification/dist/index.css'

import { client, clientLowPrio, clientRateLimit, clientProxy } from '@/services/apolloclient'
import VueApollo from 'vue-apollo'

import mixpanel from 'mixpanel-browser'
mixpanel.init(process.env.VUE_APP_MIX_PANEL_TOKEN, {
  api_host: process.env.VUE_APP_MIX_PANEL_API_HOST || 'https://api-js.mixpanel.com',
  debug: process.env.VUE_APP_MIX_PANEL_DEBUG,
  persistence: 'localStorage',
  cross_subdomain_cookie: false,
})

if (process.env.VUE_APP_CLARITY_ENABLED === 'true') {
  // eslint-disable-next-line no-extra-semi
  ;(function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        // eslint-disable-next-line no-extra-semi
        ;(c[a].q = c[a].q || []).push(arguments)
      }
    t = l.createElement(r)
    t.async = 1
    t.src = 'https://www.clarity.ms/tag/' + i
    y = l.getElementsByTagName(r)[0]
    y.parentNode.insertBefore(t, y)
  })(window, document, 'clarity', 'script', process.env.VUE_APP_CLARITY_PROJECT_ID)
}

import { initDataDogLogs, initDataDogRum } from './services/datadog'
initDataDogLogs()

// TECHDEBT: enhancement: can implement own IntersectionObserver
import VueObserveVisibility from 'vue-observe-visibility'

Vue.use(VueObserveVisibility)

Vue.use(Toast)
Vue.use(McToastAlertPlugin)
Vue.use(McMessageDialogPlugin)
Vue.use(PortalVue)

Vue.use(VueRouter)
const VueScrollTo = require('vue-scrollto')
Vue.use(vco)
Vue.use(VueScrollTo)
Vue.config.errorHandler = (err, vm, info) => {
  // err: error trace
  // vm: component in which error occured
  // info: Vue specific error information such as lifecycle hooks, events etc.
  // TODO: Perform any custom logic or log to server
  if (info === 'nextTick') {
    window.location.reload()
  }

  // eslint-disable-next-line no-console
  console.error(err)
}

Vue.use(BootstrapVue)
Vue.use(Vuelidate)
Vue.use(VueSweetalert2)
Vue.component('v-select', vSelect)
Vue.component('McLoading', McLoading)
Vue.component('McMessageDialog', McMessageDialog)
Vue.use(DatePicker)
Vue.component('GmapCluster', GmapCluster)
Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAP_API_KEY,
    libraries: 'places,geometry,drawing',
    place: 'geometry,drawing',
  },
  installComponents: true,
})

// Tell Vue to install the plugin.
Vue.use(VuejsDialog, {
  html: false,
  loader: true,
  okText: 'Ya',
  cancelText: 'Tidak',
  animation: 'bounce',
  backdropClose: true,
})
Vue.use(VueApollo)
const apolloProvider = new VueApollo({
  defaultClient: client,
  clients: {
    client,
    clientLowPrio,
    clientRateLimit,
    clientProxy,
  },
})

Vue.use(FormulatePlugin)

import VueLottiePlayer from 'vue-lottie-player'

Vue.use(VueLottiePlayer)

import { formatNumber } from './helpers/format-number'

Vue.filter('formatNumber', formatNumber)

import './registerServiceWorker'

// import { initRemoteConfig } from './services/flags'
import { initFreshchat } from './services/startFreshchat'
import { Cache } from './services/cache'
import { isTemporaryLiveTrackPage } from './helpers/public-tracking'
/**
 * Adds a new Cache instance to the Vue prototype, allowing it to be accessed globally throughout the application.
 * The Cache instance is initialized with a maximum size of 50 entries.
 */
Vue.prototype.$cache = new Cache(50)

function mountApp() {
  const vue = new Vue({
    router,
    store,
    i18n,
    apolloProvider,
    render: (h) => h(App),
  })
  vue.$mount('#app')
}

if (isTemporaryLiveTrackPage()) {
  store
    .dispatch('identity/getFeatureFlag')
    .finally(() => {
      mountApp()
    })
} else {
  store
    .dispatch('identity/init')
    .then(async (data) => {
      store.dispatch('identity/getNewFeatures')
      localStorage.setItem('wsToken', data?.wsToken)
      store
        .dispatch('identity/getFeatureFlag')
        .then(async () => {
          const freshChatSupportFlag = store.state.identity.featureFlag.find((feature) => {
            return feature.key.includes('freshchat_support')
          })
          if (freshChatSupportFlag && freshChatSupportFlag.value) {
            store.dispatch('freshchat/showNotifFreshchat', data)
            await initFreshchat(data, store)
          }

          const dataDogRumFlag = store.state.identity.featureFlag.find(
            (feature) => feature.key == 'dataDogRumFlag'
          )
          if (dataDogRumFlag && dataDogRumFlag.value) {
            const dataDogRumConfig = store.state.identity.featureFlag.find(
              (feature) => feature.key == 'dataDogRumConfig'
            )
            if (dataDogRumConfig) {
              initDataDogRum(dataDogRumConfig.value)
            }
          }

          const hasAccessMultiVoip = store.getters['identity/hasAccess']([
            'b3p-trackvision-multivoip:read',
          ])

          if (hasAccessMultiVoip) {
            const maxConcurrent = store.getters['identity/getFeatureFlagValue'](
              'trackvision_voip_max_concurrent'
            )
            store.commit('voip/setMaxCall', maxConcurrent)
          }
        })
        .finally(() => {
          mountApp()
        })
    })
    .catch((e) => {
      /**
       * TODO: Show error page
       */
      // eslint-disable-next-line no-console
      console.error(e)
      // prevent app blank
      mountApp()
    })
}
